/** Founder-confirmed Privacy Policy & Children's Privacy Notice (COPPA). Version 1.0. */

export const PRIVACY_PATH = '/privacy';
export const PRIVACY_TITLE = "DataRook Privacy Policy & Children's Privacy Notice";
export const PRIVACY_EFFECTIVE_DATE = 'September 6, 2026';
export const PRIVACY_VERSION = '1.0';
export const OPERATOR_NAME = 'DataRook, Inc.';
export const OPERATOR_STREET = '121 E Jackson Ave';
export const OPERATOR_CITY_LINE = 'Knoxville, TN 37915';
export const OPERATOR_ADDRESS = `${OPERATOR_STREET}, ${OPERATOR_CITY_LINE}`;
export const OPERATOR_EMAIL = 'info@datarook.com';
export const MAILTO_HREF = `mailto:${OPERATOR_EMAIL}`;

const emailLink = `[${OPERATOR_EMAIL}](${MAILTO_HREF})`;

export const introParagraphs = [
  `${OPERATOR_NAME} ("DataRook," "we," "us") runs footyLab — programs where students play soccer, collect their own performance data, and learn data science by analyzing it. Because most of the students we serve are under 13, this policy is written for parents and guardians first, and it doubles as our notice under the Children's Online Privacy Protection Act (COPPA).`,
  'This policy covers:',
];

export const coversList = [
  'our website, [datarook.com](https://datarook.com);',
  'the **footyLab web app** students use during programs;',
  'our **in-person programs** (after-school programs, camps, and clinics), including the wearable GPS trackers and cameras we use on the field; and',
  '**registration**, whether you register through our forms or through a partner organization (a school, YMCA, Boys & Girls Club, or similar).',
];

export const introClose = `If anything here is unclear, email us at ${emailLink}. We answer within two business days.`;

export const shortVersionItems = [
  '**We collect only what we need to run a safe program and teach the class.** Registration details from you; a footyLab account, the code and projects your student creates, GPS performance data, and (with your separate permission) photos and video from us.',
  '**We never sell personal information, never show ads, and never build marketing profiles of students.**',
  '**Students are identified inside our systems by a code (for example, FLC-142), not by name, wherever we can manage it.**',
  "**You can see, correct, or delete your student's information at any time**, and you can withdraw permission for any optional use (such as photos) without affecting your student's place in the program.",
  'We keep information only as long as the schedule in [Section 8](#section-8) says, then delete it.',
];

export const sections = [
  {
    id: 'section-1',
    number: '1',
    title: 'Who is responsible for your information',
    blocks: [
      { type: 'operator' },
      {
        type: 'p',
        text: 'DataRook is the "operator" of footyLab under COPPA. When a partner organization hosts a program (for example, a YMCA after-school site), the partner is responsible for its own registration systems and records; this policy covers what DataRook collects and holds.',
      },
    ],
  },
  {
    id: 'section-2',
    number: '2',
    title: 'Information we collect',
    blocks: [
      {
        type: 'h3',
        id: 'section-2-1',
        text: '2.1 From you (the parent or guardian) at registration',
      },
      {
        type: 'table',
        headers: ['What', 'Why we need it'],
        rows: [
          [
            "Student's first name and last initial (and last name where DataRook runs the program directly)",
            'Roster, attendance, waiver, emergency care',
          ],
          [
            "Student's grade and age",
            'Grouping, curriculum level, and to know whether COPPA applies',
          ],
          [
            'Your name, email, and phone; an emergency contact',
            'Program communication, pickup, emergencies',
          ],
          [
            'Allergies, medical needs, or learning/accessibility notes you choose to share',
            'Keeping your student safe and supported on the field and in the lab',
          ],
          [
            'Your choices: media release, communication preferences, the liability waiver',
            'To honor your decisions',
          ],
          ['T-shirt size and school (some programs)', 'Logistics only'],
        ],
      },
      {
        type: 'p',
        text: 'We do **not** ask for Social Security numbers, home addresses (unless required for a specific program), or exact dates of birth.',
      },
      {
        type: 'h3',
        id: 'section-2-2',
        text: '2.2 From your student during the program',
      },
      {
        type: 'table',
        headers: ['What', 'How it is collected', 'Why'],
        rows: [
          [
            '**footyLab account** — a student code (e.g., FLC-142), a display name/avatar the student chooses, and a password (stored only as a one-way hash)',
            'Set up by a coach; password chosen by the student',
            'So each student can log in and see their own data',
          ],
          [
            '**Student work** — Python code, charts, dashboards, and "published" projects',
            'Created by the student in the app',
            'Learning, feedback, and portfolio; anonymized examples may be featured in class',
          ],
          [
            '**GPS performance data** — distance, speed, sprints, accelerations, and similar metrics from a wearable tracker worn during soccer sessions; in some programs, heart rate',
            'Catapult GPS tracker worn on a vest',
            'The dataset each student analyzes; coaching feedback',
          ],
          [
            '**Video** — session footage from a fixed field camera and short highlight clips a student chooses to upload to their profile',
            'Camera at the field; student upload',
            "Reviewing play; highlights for the student's own dashboard",
          ],
          [
            '**Short surveys** — interests, confidence with soccer and STEM, comfort with English (to support bilingual students)',
            'Completed in class',
            'Tailoring instruction; program improvement',
          ],
          [
            '**Session and usage records** — a login cookie, which app features a student used and when',
            'Automatically, in the app',
            "Keeping students logged in securely; understanding what works in the curriculum. These records use the student code, not the student's name, and never include the content of the student's code.",
          ],
        ],
      },
      {
        type: 'p',
        text: 'We do **not** collect precise real-time location outside of soccer sessions, and the GPS trackers are collected at the end of every session.',
      },
      {
        type: 'h3',
        id: 'section-2-3',
        text: '2.3 From partner organizations',
      },
      {
        type: 'p',
        text: 'When a school or community partner handles registration, it may give us a roster with the fields in 2.1 (usually name, grade, and a parent contact). We ask partners to share only what is needed and to give families this notice.',
      },
      {
        type: 'h3',
        id: 'section-2-4',
        text: '2.4 On [datarook.com](https://datarook.com)',
      },
      {
        type: 'p',
        text: 'Our website collects what you send through the contact form (name, email, message), standard server logs, and Google Analytics 4 (pages viewed, approximate location, and device). We do not run advertising or retargeting pixels on datarook.com. The footyLab web app does not use Google Analytics.',
      },
    ],
  },
  {
    id: 'section-3',
    number: '3',
    title: 'How we use information',
    blocks: [
      {
        type: 'ul',
        items: [
          'Run the program: rosters, attendance, grouping, emergency care, communication with you.',
          'Deliver the curriculum: give each student their own data to analyze, save their work, and show their progress.',
          'Keep students safe online: authentication and security monitoring.',
          'Improve footyLab: aggregate, de-identified analysis of which activities work.',
          'Program reporting to the host partner or funder — **in aggregate or de-identified form** (for example, "18 students completed the program; average confidence rose 1.2 points"), never individual student records unless the partner already holds them and the parent has been told.',
          'Research, **only** with a separate, signed research consent (see [Section 7](#section-7)).',
          'Legal and safety obligations.',
        ],
      },
      {
        type: 'p',
        text: 'We do **not** use student information for targeted advertising, do not sell or rent it, and do not use it to build a profile of a student for any purpose other than the program itself.',
      },
    ],
  },
  {
    id: 'section-4',
    number: '4',
    title: 'Children under 13 — our COPPA notice and your consent',
    blocks: [
      {
        type: 'p',
        text: "footyLab programs include children under 13, so we apply COPPA's protections to every participant.",
      },
      {
        type: 'p',
        text: "**Consent.** Before a student under 13 uses the footyLab app or wears a tracker, we need a parent or guardian's verifiable consent. You give it by completing our registration form (which includes this notice and a consent checkbox) and either paying for the program or returning a signed form; where a partner registers families, the partner collects the same consent on our behalf or confirms that it has done so.",
      },
      {
        type: 'p',
        text: '**Separate consent for optional uses.** Photos and video used outside the classroom (website, social media, flyers, funder reports) and participation in research each require a separate "yes." Saying no to either has no effect on your student\'s participation.',
      },
      {
        type: 'p',
        text: '**We collect no more than is reasonably necessary** for a student to take part, and we never condition participation on giving more information than that.',
      },
      {
        type: 'p',
        text: '**Your rights.** At any time you may:',
      },
      {
        type: 'ol',
        items: [
          '**Review** the personal information we hold about your student.',
          '**Correct** it.',
          "**Delete** it and/or **refuse further collection or use**. If you withdraw consent for the app or trackers, your student can still take part in the on-field and unplugged activities; some lab activities depend on the student's own data and may not be available.",
        ],
      },
      {
        type: 'p',
        text: `Send requests to ${emailLink} from the email address you registered with, or ask your coach. We verify you are the parent or guardian (usually by matching your registration details) and respond within 30 days.`,
      },
      {
        type: 'p',
        text: '**Schools.** When a public school contracts with us and provides student information for an educational purpose, the school may authorize collection in place of individual parental consent, as COPPA permits; we use that information only for the school-authorized purpose and give the school the same review and deletion rights you have.',
      },
    ],
  },
  {
    id: 'section-5',
    number: '5',
    title: 'Who we share information with',
    blocks: [
      {
        type: 'p',
        text: 'We share personal information only with the companies that help us run footyLab, with your program\'s host partner, and as the law requires. Each service provider is bound by contract to use the information only to provide its service to us and to protect it.',
      },
      {
        type: 'table',
        headers: ['Provider', 'What they do for us', 'What they may hold'],
        rows: [
          [
            '**Notion Labs, Inc.**',
            'Rosters, registration responses, student accounts and saved work, program records, and the contact form on [datarook.com](https://datarook.com)',
            'Registration details, student codes and display names, hashed passwords, student work, and website inquiry fields (name, email, message)',
          ],
          [
            '**DigitalOcean, LLC**',
            'Hosts the footyLab app and stores uploaded files',
            'Student codes, session records, GPS files, highlight videos, student work',
          ],
          [
            '**Catapult Sports**',
            'Wearable GPS trackers and the cloud service that processes them',
            'GPS and movement data; the athlete label we assign (student code where possible)',
          ],
          ['**Veo Technologies**', 'Field camera and video hosting', 'Session video'],
          [
            '**Google (Workspace)**',
            'Email, documents, spreadsheets, and forms for some programs; transactional website-inquiry notices and acknowledgments',
            'Registration responses, program documents, and inquiry notification or acknowledgment email',
          ],
          [
            '**Google Analytics (Google)**',
            'Measures how people use [datarook.com](https://datarook.com)',
            'Usage data from the marketing website. We do not use Google Analytics on the footyLab web app',
          ],
          [
            '**Stripe, Inc.**',
            'Payment processing for programs DataRook bills directly',
            'Your payment details (DataRook never sees full card numbers)',
          ],
          [
            '**Tally**',
            'Previously received website contact forms on [datarook.com](https://datarook.com)',
            'Whatever was typed into the former contact form (records we still hold)',
          ],
          [
            '**YouTube (Google)**',
            'Embedded program videos on our pages; on occasion, a private, invite-only live stream for families',
            'If a live stream is used for your program, we will tell you first and it is covered by the media release',
          ],
        ],
      },
      {
        type: 'p',
        text: '**Host partners** (the school, YMCA, club, or nonprofit hosting your program) receive attendance and aggregate results, and we share individual information with them only when they already hold it (for example, a partner that ran registration) or when safety requires it.',
      },
      {
        type: 'p',
        text: '**Research partners.** See [Section 7](#section-7). **Legal.** We disclose information when required by law, to protect a child\'s safety, or to respond to a valid legal request. **Business transfers.** If DataRook is ever acquired, information will remain covered by this policy or you will be notified and given a choice.',
      },
      {
        type: 'p',
        text: 'We will update this list when providers change. We do not allow any provider to use student information for its own marketing.',
      },
    ],
  },
  {
    id: 'section-6',
    number: '6',
    title: 'Photos, video, and student work',
    blocks: [
      {
        type: 'ul',
        items: [
          "**Field camera footage** is used inside the program for coaching and analysis and is stored with our video provider under DataRook's account.",
          '**Highlight clips** a student uploads are visible only to that student and their coaches inside the app, through time-limited links.',
          '**Anything public** — website, social media, flyers, presentations, or reports to funders — requires your separate "yes" on the media release, and we never publish a student\'s full name, school, or contact details alongside their image.',
          "**Student work** is the student's. Examples we show publicly are anonymized (student code or first name only, and only with the media release).",
        ],
      },
      {
        type: 'p',
        text: 'You can change your media release decision at any time by emailing us; we will stop new uses and remove content we control within 30 days.',
      },
    ],
  },
  {
    id: 'section-7',
    number: '7',
    title: 'Research',
    blocks: [
      {
        type: 'p',
        text: "DataRook works with university researchers (currently the University of Tennessee, Knoxville, under an IRB-approved protocol) to study how programs like footyLab affect learning. Participation in research is **optional and separate** from participation in footyLab. It requires a signed parent consent and student assent; research data are coded, stored under the university's data-security plan, and governed by that consent form in addition to this policy.",
      },
    ],
  },
  {
    id: 'section-8',
    number: '8',
    title: 'How long we keep information',
    blocks: [
      {
        type: 'p',
        text: "We keep personal information only as long as needed for the purposes above, then delete it on the following schedule. We never keep children's personal information indefinitely.",
      },
      {
        type: 'table',
        headers: ['Information', 'Kept until', 'Then'],
        rows: [
          [
            'Registration details (names, contacts, grade, age, choices)',
            '12 months after the program ends (so returning families do not have to re-enter everything)',
            'Deleted',
          ],
          [
            'Allergy, medical, and support notes',
            '30 days after the program ends',
            'Deleted',
          ],
          [
            'Roster link between student code and name',
            "12 months after the student's last program",
            'Deleted',
          ],
          [
            'Passwords and login sessions',
            'Sessions expire after 12 hours; password hashes are deleted with the roster',
            'Deleted',
          ],
          [
            'GPS performance data',
            'End of the program (an anonymized copy may be kept for curriculum examples)',
            'Deleted from tracker accounts and storage',
          ],
          [
            'Field camera video and highlight clips',
            '12 months after the program ends, unless your media release allows longer for a specific use',
            'Deleted',
          ],
          [
            'Student code, projects, and dashboards',
            "24 months after the student's last program (so students can return to their work)",
            'Deleted; anonymized examples may be kept',
          ],
          [
            'App usage records',
            '24 months, in pseudonymous form',
            'Deleted; aggregate statistics kept',
          ],
          [
            'Consent and media-release records',
            '3 years after the related information is deleted',
            'Deleted',
          ],
          [
            'Payment records',
            'As required by tax and accounting law (Stripe holds card data)',
            'Deleted',
          ],
        ],
      },
      {
        type: 'p',
        text: 'Backups are retained for up to 35 days and then expire. You can request earlier deletion at any time ([Section 4](#section-4)).',
      },
    ],
  },
  {
    id: 'section-9',
    number: '9',
    title: 'How we protect information',
    blocks: [
      {
        type: 'p',
        text: 'We maintain a written information-security program appropriate to the sensitivity of children\'s data, reviewed at least annually. It includes: encryption of data in transit and at rest; passwords stored only as one-way hashes; multi-factor authentication for every staff account; access limited to the coaches and staff who need it, reviewed each term; pseudonymous student codes in place of names wherever possible; private storage with time-limited links for video and files; logging of access to student records; background checks for all staff and interns who work with students; and written security commitments from every provider in [Section 5](#section-5). No system is perfectly secure, but if we ever learn that student information has been exposed, we will notify affected families and our host partner promptly and in any case within the time required by Tennessee law.',
      },
    ],
  },
  {
    id: 'section-10',
    number: '10',
    title: 'Your choices and rights (all families)',
    blocks: [
      {
        type: 'p',
        text: `Whatever state you live in, you can ask us to access, correct, delete, or stop using personal information about you or your student, and to tell you what we hold and who we have shared it with. We do not sell personal information or use it for targeted advertising, so there is nothing to opt out of on that front. We will not treat you differently for exercising these rights. Email ${emailLink}; we respond within 30 days and will explain if we cannot honor a request in full (for example, records we must keep for tax purposes).`,
      },
    ],
  },
  {
    id: 'section-11',
    number: '11',
    title: 'Cookies and similar technologies',
    blocks: [
      {
        type: 'p',
        text: 'The footyLab app sets one strictly necessary cookie to keep a student logged in; it expires after 12 hours and contains no personal information. The footyLab app does not use Google Analytics. datarook.com uses Google Analytics 4, which sets cookies to measure how the marketing site is used. The contact form on datarook.com (Notion) and embedded YouTube videos (after you choose to play them) may set their own cookies under their own policies. Stripe checkout, when used for a program, may also set cookies under its own policy. We do not use advertising or retargeting pixels.',
      },
    ],
  },
  {
    id: 'section-12',
    number: '12',
    title: 'Changes to this policy',
    blocks: [
      {
        type: 'p',
        text: 'If we make a material change — in particular, any new type of information collected from children or any new way we share it — we will notify registered families by email and, where COPPA requires, obtain fresh consent before the change applies to their student. The effective date at the top always shows the current version.',
      },
    ],
  },
  {
    id: 'section-13',
    number: '13',
    title: 'Contact us',
    blocks: [{ type: 'operator', inline: true }],
  },
];

export const appendixTitle =
  'Appendix — Direct Notice to Parents (for registration confirmations and partner handouts)';

export const appendixLead = "Your student's information at footyLab — what you need to know";

export const appendixParagraphs = [
  `${OPERATOR_NAME}, ${OPERATOR_ADDRESS} (${emailLink}) runs footyLab. To take part, your student will get a footyLab account identified by a code (not their name), wear a GPS tracker during soccer sessions, and create code and charts in our web app. We collect: the registration details you gave us; your student's GPS performance data; the work they create; session video from a field camera; and a login cookie. We use this only to run the program and teach the class. We share it only with the service providers that run our systems (listed at [datarook.com/privacy](${PRIVACY_PATH})), with the organization hosting your program, and as required by law. We never sell it or use it for advertising. We keep it only as long as our published retention schedule allows.`,
  'By registering, you consent to this collection and use for your student. Photos or video used outside the classroom, and participation in research, each need your separate permission and are never required.',
  `You can review, correct, or delete your student's information, or stop us collecting more, at any time by emailing ${emailLink}. Full policy: [datarook.com/privacy](${PRIVACY_PATH}).`,
];

const stripMarkup = (value) =>
  String(value)
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

const collectBlockText = (block) => {
  if (block.type === 'p' || block.type === 'h3') {
    return stripMarkup(block.text);
  }
  if (block.type === 'ul' || block.type === 'ol') {
    return block.items.map(stripMarkup).join('\n');
  }
  if (block.type === 'table') {
    return [...block.headers, ...block.rows.flat()].map(stripMarkup).join('\n');
  }
  if (block.type === 'operator') {
    return `${OPERATOR_NAME}\n${OPERATOR_ADDRESS}\n${OPERATOR_EMAIL}`;
  }
  return '';
};

export const privacyPolicyPlainText = () =>
  [
    PRIVACY_TITLE,
    PRIVACY_EFFECTIVE_DATE,
    PRIVACY_VERSION,
    ...introParagraphs.map(stripMarkup),
    ...coversList.map(stripMarkup),
    stripMarkup(introClose),
    ...shortVersionItems.map(stripMarkup),
    ...sections.flatMap((section) => [
      `${section.number}. ${section.title}`,
      ...section.blocks.map(collectBlockText),
    ]),
    appendixTitle,
    appendixLead,
    ...appendixParagraphs.map(stripMarkup),
  ].join('\n');
